import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ITraceReasoningProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    getTraceDurationText,
    useTraceOpen
} from "../../util";
import type {
    TraceStatus
} from "../../type";
import {
    Brain as BrainIcon
} from "lucide-react-native";
import {
    TraceContext
} from "../../context";
import MarkdownViewer from "../../../markdownViewer";
import TraceCaret from "../../internal/caret";
import TraceCollapsible from "../collapsible";
import TraceHead from "../../internal/head";
import TraceContent from "../content";
import TraceTrigger from "../trigger";
import TraceLine from "../line";

const TraceReasoning: FC<ITraceReasoningProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    isOpen: isOpenProp,
    status = "running",
    durationSeconds,
    isDefaultOpen,
    onOpenChange,
    onStopPress,
    style,
    text,
    ...props
}) => {
    const traceContext = useContext(TraceContext);

    const customLocalize = customLocalizeProp ?? traceContext?.customLocalize;

    const customTheme = customThemeProp ?? traceContext?.customTheme;

    const {
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isRunning = status === "running";

    const {
        handleOpenChange,
        isOpen
    } = useTraceOpen({
        isAutoOpenWhileRunning: true,
        isOpen: isOpenProp,
        isDefaultOpen,
        onOpenChange,
        isRunning
    });

    const durationText = durationSeconds === undefined ? undefined : getTraceDurationText({
        seconds: durationSeconds,
        locale: activeLocale,
        localize
    });

    const getTitle = (): string => {
        if(status === "error") {
            return localize("thinking-failed");
        }

        if(status === "cancelled") {
            return localize("thinking-stopped");
        }

        if(isRunning) {
            return localize("thinking");
        }

        return durationText ? localize("thought-for-x", [
            durationText
        ]) : localize("thought");
    };

    const lineStatus: TraceStatus = status;

    const renderBrainIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <BrainIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const renderLine = () => {
        return <TraceLine
            description={status === "done" ? undefined : durationText}
            customTheme={customTheme}
            icon={renderBrainIcon}
            isShimmer={isRunning}
            status={lineStatus}
            title={getTitle()}
        />;
    };

    const stopHandler = isRunning ? onStopPress : undefined;

    if(!text && !isRunning) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container
            ]}
        >
            <TraceHead
                customLocalize={customLocalize}
                customTheme={customTheme}
            >
                {renderLine()}
            </TraceHead>
        </View>;
    }

    return <TraceCollapsible
        {...props}
        onOpenChange={handleOpenChange}
        customTheme={customTheme}
        isOpen={isOpen}
        style={style}
    >
        <TraceTrigger
            customLocalize={customLocalize}
            customTheme={customTheme}
            onStopPress={stopHandler}
        >
            {renderLine()}
        </TraceTrigger>
        <TraceContent
            customTheme={customTheme}
            aria-live="off"
        >
            {
                text ?
                    <MarkdownViewer
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                        customTextProps={{
                            "<p>": {
                                color: "mid"
                            }
                        }}
                        content={text}
                    />
                :
                    null
            }
            {
                isRunning ?
                    <TraceCaret
                        customTheme={customTheme}
                    />
                :
                    null
            }
        </TraceContent>
    </TraceCollapsible>;
};
export default TraceReasoning;
